// Video Studio — toplu video kuyrugu.
//
// Kuyruk SUNUCUDA durur (video_queue_items); uretimi bu bilgisayarda calisan
// yerel worker yapar (tools/video-worker): siradaki isi alir, ComfyUI'de
// uretir, videoyu yukler. Bu yuzden sayfa kapali olsa da kuyruk surer; panel
// yalnizca kuyruga ekler, durumu gosterir ve onay verir.
//
// Yalnizca kargo profili farkli olan listeler (ayni baslik + ayni ana gorsel;
// Toplu Cogalt / coklu kargo ile acilanlar) TEK grup sayilir: bir video
// uretilir, onaylaninca gruptaki tum taslaklara yazilir. Grupta videosu olan
// varsa uretim yapilmaz, o video eksik olanlara kopyalanir.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ListVideo, Loader2, Play, RefreshCw, CheckCircle2, AlertCircle, Truck, ThumbsUp, ThumbsDown, Eye,
  Cpu, KeyRound, Copy, Check, Trash2, RotateCcw,
} from "lucide-react";
import { ComfyFileLink } from "./ComfyFileLink";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { buildPrompt, getComfyUrl, outputUrl, type ApiWorkflow } from "../../lib/comfyui";
import { JEWELRY_WISHES, PRODUCT_PRESETS, negativeFor } from "../../lib/ltxPresets";
import { detectPreset } from "../../lib/ltxDetect";

/** Kuyrugun kullandigi ayarlar — Video Studio'nun guncel (hatirlanan) secimleri */
export interface QueueSettings {
  template: ApiWorkflow;
  vertical: string;
  wishId: string;
  width: number;
  height: number;
  frames: number;
  fps: number;
  imgCompression: number;
  withAudio: boolean;
  strengthOverride: number | null;
  negativeOverride: string | null;
  /** Panelde okunur ozet (oran · kalite · sure ...) */
  summary: string;
  /** ComfyUI cikti klasoru (dosya yolunu gostermek icin) */
  outputDir: string;
}

interface DraftLite {
  id: string;
  title: string;
  status: string;
  thumbUrl: string | null;
  videoUrl?: string | null;
}

interface Group {
  key: string;
  title: string;
  imageUrl: string;
  drafts: DraftLite[];
  /** Grupta zaten var olan video (varsa uretilmez, eksiklere kopyalanir) */
  videoUrl: string | null;
}

interface QueueItem {
  id: string; groupKey: string; title: string; imageUrl: string; draftIds: string[]; summary: string | null;
  status: "pending" | "processing" | "review" | "error"; progress: string | null; error: string | null;
  attempts: number; videoUrl: string | null; comfyFilename: string | null; comfySubfolder: string | null;
}
interface WorkerInfo { hasKey: boolean; lastSeenAt: string | null; comfy: string | null }

const abs = (u: string) => (u.startsWith("http") || u.startsWith("data:") ? u : `${getBackendUrl()}${u}`);
const norm = (t: string) => t.trim().toLowerCase().replace(/\s+/g, " ");
/** Worker gorseli kendisi yukleyip bu yer tutucunun yerine yazar */
const IMAGE_PLACEHOLDER = "__ERC_IMAGE__";

export function BulkVideoQueue({ settings }: { settings: QueueSettings }) {
  const [drafts, setDrafts] = useState<DraftLite[] | null>(null);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [worker, setWorker] = useState<WorkerInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enqueuing, setEnqueuing] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadDrafts = useCallback(async () => {
    try {
      const res = await fetch(`${getBackendUrl()}/api/listing-drafts?light=1&all=1`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Listeler alınamadı (${res.status})`);
      setDrafts(((await res.json()).data ?? []) as DraftLite[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setDrafts([]);
    }
  }, []);
  const loadQueue = useCallback(async () => {
    try {
      const res = await fetch(`${getBackendUrl()}/api/video-queue`, { headers: authHeaders() });
      if (!res.ok) return;
      const d = (await res.json()).data as { items: QueueItem[]; worker: WorkerInfo };
      setItems(d.items ?? []);
      setWorker(d.worker);
    } catch { /* sonraki turda tekrar */ }
  }, []);
  useEffect(() => { void loadDrafts(); void loadQueue(); }, [loadDrafts, loadQueue]);
  // Kuyruk durumu sunucudan izlenir (sayfa gorunurken)
  useEffect(() => {
    const t = window.setInterval(() => { if (document.visibilityState === "visible") void loadQueue(); }, 5000);
    return () => window.clearInterval(t);
  }, [loadQueue]);

  const groups = useMemo<Group[]>(() => {
    const map = new Map<string, Group>();
    for (const d of drafts ?? []) {
      if (d.status === "archived" || !d.thumbUrl) continue;
      const key = `${norm(d.title)}|${d.thumbUrl}`;
      const g = map.get(key) ?? { key, title: d.title, imageUrl: d.thumbUrl, drafts: [], videoUrl: null };
      g.drafts.push(d);
      if (!g.videoUrl && d.videoUrl) g.videoUrl = d.videoUrl;
      map.set(key, g);
    }
    return [...map.values()];
  }, [drafts]);
  const queued = new Set(items.map((x) => x.groupKey));
  const missing = groups.filter((g) => g.drafts.some((d) => !d.videoUrl));
  const toCopy = missing.filter((g) => g.videoUrl);
  const toQueue = missing.filter((g) => !g.videoUrl && !queued.has(g.key));
  const review = items.filter((x) => x.status === "review");
  const counts = {
    pending: items.filter((x) => x.status === "pending").length,
    processing: items.filter((x) => x.status === "processing").length,
    error: items.filter((x) => x.status === "error").length,
  };

  /** Grubun ComfyUI workflow'u: tekli uretimdeki kurallarin aynisi */
  async function workflowFor(g: Group): Promise<ApiWorkflow> {
    const s = settings;
    let preset = PRODUCT_PRESETS.find((p) => p.id === detectPreset({ vertical: s.vertical as never, title: g.title }).presetId) ?? PRODUCT_PRESETS[0];
    const wish = preset.id === "taki" ? JEWELRY_WISHES.find((w) => w.id === s.wishId) ?? JEWELRY_WISHES[0] : null;
    let scene: string;
    if (wish) scene = wish.scene;
    else {
      // Kayitli analiz varsa API onu dondurur (ucretsiz, hizli)
      const r = await fetch(`${getBackendUrl()}/api/media/describe-scene`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ imageUrl: abs(g.imageUrl), productHint: g.title }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.message || b.error || "Sahne analizi başarısız");
      scene = b.data.scene;
      if (b.data.occluded) preset = PRODUCT_PRESETS.find((p) => p.id === "kapali") ?? preset;
    }
    return buildPrompt(s.template, {
      imageName: IMAGE_PLACEHOLDER,
      prompt: `${scene.trim()} ${wish ? "" : preset.promptSuffix}`.trim(),
      negativePrompt: s.negativeOverride ?? negativeFor(preset),
      width: s.width, height: s.height, frames: s.frames, fps: s.fps,
      withAudio: s.withAudio, strength: s.strengthOverride ?? wish?.strength ?? preset.strength,
      imgCompression: s.imgCompression,
    });
  }

  /** Videosu olan gruptan eksiklere kopyala, kalanlari sunucu kuyruguna ekle */
  async function enqueue() {
    setError(null);
    try {
      for (const [i, g] of toCopy.entries()) {
        setEnqueuing(`Var olan videolar kopyalanıyor ${i + 1}/${toCopy.length}`);
        for (const d of g.drafts.filter((x) => !x.videoUrl)) {
          await fetch(`${getBackendUrl()}/api/listing-drafts/${d.id}`, {
            method: "PATCH", headers: authHeaders(), body: JSON.stringify({ videoUrl: g.videoUrl }),
          });
        }
      }
      let batch: Array<Record<string, unknown>> = [];
      const flush = async () => {
        if (!batch.length) return;
        const r = await fetch(`${getBackendUrl()}/api/video-queue`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ items: batch }) });
        if (!r.ok) throw new Error(`Kuyruğa eklenemedi (${r.status})`);
        batch = [];
      };
      for (const [i, g] of toQueue.entries()) {
        setEnqueuing(`Kuyruğa hazırlanıyor ${i + 1}/${toQueue.length}`);
        batch.push({
          groupKey: g.key, title: g.title, imageUrl: abs(g.imageUrl), draftIds: g.drafts.map((d) => d.id),
          workflow: await workflowFor(g), summary: settings.summary,
        });
        if (batch.length >= 10) await flush();
      }
      await flush();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setEnqueuing(null);
      void loadDrafts(); void loadQueue();
    }
  }

  async function act(item: QueueItem, path: string, method = "POST") {
    setBusyId(item.id);
    try {
      const r = await fetch(`${getBackendUrl()}/api/video-queue/${item.id}${path}`, { method, headers: authHeaders() });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `İşlem başarısız (${r.status})`);
      if (path === "/approve") void loadDrafts();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
      void loadQueue();
    }
  }

  async function cancelPending() {
    if (!confirm(`${counts.pending} bekleyen iş kuyruktan çıkarılsın mı?`)) return;
    await fetch(`${getBackendUrl()}/api/video-queue/cancel-pending`, { method: "POST", headers: authHeaders() });
    void loadQueue();
  }

  async function createKey() {
    if (worker?.hasKey && !confirm("Yeni anahtar oluşturulursa eski config.json çalışmaz. Devam?")) return;
    const r = await fetch(`${getBackendUrl()}/api/video-queue/worker-key`, { method: "POST", headers: authHeaders() });
    const j = await r.json().catch(() => ({}));
    if (r.ok) { setNewKey(j.data.key); void loadQueue(); } else setError(j.error || "Anahtar oluşturulamadı");
  }

  const configJson = newKey ? JSON.stringify({ apiUrl: getBackendUrl(), workerKey: newKey, comfyUrl: getComfyUrl() }, null, 2) : "";
  const seenAgo = worker?.lastSeenAt ? (Date.now() - new Date(worker.lastSeenAt).getTime()) / 1000 : null;
  const workerOnline = seenAgo != null && seenAgo < 60;

  return (
    <div className="mb-6 rounded-lg border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold"><ListVideo className="h-4 w-4 text-indigo-600" /> Toplu video kuyruğu</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {drafts === null ? "Listeler yükleniyor…"
              : `${drafts.length} liste · ${groups.length} grup · ${toQueue.length} kuyruğa alınabilir${toCopy.length ? ` · ${toCopy.length} grupta var olan video kopyalanacak` : ""}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { void loadDrafts(); void loadQueue(); }} title="Yenile" className="rounded-lg border p-2 hover:bg-gray-50">
            <RefreshCw className="h-4 w-4" />
          </button>
          <button onClick={() => void enqueue()} disabled={!!enqueuing || (toQueue.length === 0 && toCopy.length === 0)}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-40">
            {enqueuing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            {enqueuing ?? `Kuyruğa al (${toQueue.length})`}
          </button>
        </div>
      </div>

      <div className="space-y-1.5 px-4 py-2 text-xs text-gray-600">
        {/* Worker durumu: uretim bu bilgisayardaki worker'da */}
        <div className="flex flex-wrap items-center gap-2">
          <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${workerOnline ? (worker?.comfy ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700") : "bg-gray-100 text-gray-600"}`}>
            <Cpu className="h-3.5 w-3.5" />
            {workerOnline
              ? (worker?.comfy ? `Worker çalışıyor · ComfyUI ${worker.comfy}` : "Worker açık · ComfyUI kapalı (açılınca devam eder)")
              : worker?.lastSeenAt ? `Worker kapalı · son görülme ${new Date(worker.lastSeenAt).toLocaleTimeString("tr-TR")}` : "Worker çalışmıyor"}
          </span>
          <button onClick={() => setSetupOpen((v) => !v)} className="text-indigo-600 hover:underline">
            {setupOpen ? "Kurulumu gizle" : worker?.hasKey ? "Worker kurulumu" : "Worker kur (bir kez)"}
          </button>
        </div>
        {setupOpen && (
          <div className="space-y-2 rounded-lg border bg-gray-50 p-3">
            <p>
              Kuyruk sunucuda durur; videoyu bu bilgisayardaki <b>worker</b> üretir. Sayfa kapalı olsa da ComfyUI ve worker açık oldukça devam eder.
            </p>
            <ol className="list-decimal space-y-1 pl-4">
              <li>
                <button onClick={() => void createKey()} className="inline-flex items-center gap-1 rounded border bg-white px-2 py-0.5 font-medium hover:bg-gray-100">
                  <KeyRound className="h-3 w-3" /> {worker?.hasKey ? "Yeni anahtar oluştur" : "Anahtar oluştur"}
                </button>
                {worker?.hasKey && !newKey && <span className="ml-2 text-gray-400">(anahtar var — config.json'u kaybettiysen yenisini oluştur)</span>}
              </li>
              {newKey && (
                <li>
                  Bunu proje klasöründe <code className="rounded bg-white px-1">tools\video-worker\config.json</code> olarak kaydet:
                  <div className="relative mt-1">
                    <pre className="overflow-x-auto rounded border bg-white p-2 font-mono text-[11px]">{configJson}</pre>
                    <button onClick={() => { void navigator.clipboard.writeText(configJson); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                      className="absolute right-1.5 top-1.5 flex items-center gap-1 rounded border bg-white px-2 py-0.5 hover:bg-gray-100">
                      {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />} {copied ? "Kopyalandı" : "Kopyala"}
                    </button>
                  </div>
                  <p className="mt-1 text-amber-700">Anahtar yalnızca şimdi gösterilir.</p>
                </li>
              )}
              <li><code className="rounded bg-white px-1">tools\video-worker\start.bat</code> dosyasını çalıştır (ComfyUI ile birlikte açık kalsın).</li>
            </ol>
          </div>
        )}

        <p><span className="font-medium text-gray-700">Kuyruk ayarı:</span> {settings.summary} <span className="text-gray-400">— aşağıdaki seçimlerden; kuyruğa alınırken uygulanır, son ayar hatırlanır.</span></p>
        <p className="text-gray-400">
          Yalnızca kargo profili farklı listeler (aynı başlık + ana görsel) tek video alır; video onaylanınca hepsine yazılır.
          Etsy'de zaten yayında olan listeye video, Listing Builder'da "Görselleri Yenile" ile gider.
        </p>
        {items.length > 0 && (
          <p className="flex flex-wrap items-center gap-2 font-medium text-gray-700">
            Sunucu kuyruğu: {counts.processing ? `${counts.processing} üretiliyor · ` : ""}{counts.pending} bekliyor
            {review.length ? ` · ${review.length} onay bekliyor` : ""}{counts.error ? ` · ${counts.error} hata` : ""}
            {counts.pending > 0 && <button onClick={() => void cancelPending()} className="font-normal text-gray-400 hover:text-red-600">bekleyenleri temizle</button>}
          </p>
        )}
        {review.length > 0 && (
          <p className="flex flex-wrap items-center gap-2 font-medium text-amber-700">
            <Eye className="h-3.5 w-3.5" /> {review.length} video onay bekliyor — izleyip onayla, onaylanınca listelere uygulanır.
            <button onClick={() => void (async () => { for (const it of review) await act(it, "/approve"); })()} disabled={!!busyId}
              className="rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40">
              Hepsini onayla
            </button>
          </p>
        )}
        {error && <p className="text-red-600">{error}</p>}
      </div>

      {items.length > 0 && (
        <ul className="max-h-[32rem] divide-y overflow-y-auto border-t">
          {items.map((it) => {
            if (it.status === "review" && it.videoUrl) {
              return (
                <li key={it.id} className="bg-amber-50/40 px-4 py-3 text-xs">
                  <div className="flex gap-3">
                    <video src={abs(it.videoUrl)} controls loop muted className="h-40 w-auto shrink-0 rounded bg-black" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <Link to={`/listing-builder?id=${it.draftIds[0]}`} className="block truncate font-medium text-gray-800 hover:text-indigo-600">{it.title}</Link>
                      <p className="text-amber-700">Onay bekliyor · onaylanınca {it.draftIds.length} listeye uygulanır</p>
                      {it.comfyFilename && (
                        <ComfyFileLink url={outputUrl({ filename: it.comfyFilename, subfolder: it.comfySubfolder ?? "" })}
                          filename={it.comfyFilename} subfolder={it.comfySubfolder ?? ""} outputDir={settings.outputDir} />
                      )}
                      <div className="flex gap-2">
                        <button onClick={() => void act(it, "/approve")} disabled={busyId === it.id}
                          className="flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                          {busyId === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ThumbsUp className="h-3.5 w-3.5" />} Onayla ve uygula
                        </button>
                        <button onClick={() => void act(it, "/reject")} disabled={busyId === it.id}
                          className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                          <ThumbsDown className="h-3.5 w-3.5" /> Reddet (yeniden üret)
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            }
            return (
              <li key={it.id} className="flex items-center gap-3 px-4 py-2 text-xs">
                <img src={abs(it.imageUrl)} alt="" className="h-10 w-10 shrink-0 rounded object-cover" loading="lazy" />
                <div className="min-w-0 flex-1">
                  <Link to={`/listing-builder?id=${it.draftIds[0]}`} className="block truncate text-gray-800 hover:text-indigo-600">{it.title}</Link>
                  <p className="flex items-center gap-1 text-gray-400">
                    {it.draftIds.length > 1 && <><Truck className="h-3 w-3" /> {it.draftIds.length} liste (farklı kargo) · </>}
                    {it.attempts > 0 ? `deneme ${it.attempts}` : "sırada"}
                  </p>
                  {it.status === "error" && <p className="truncate text-red-600" title={it.error ?? ""}>{it.error}</p>}
                </div>
                <span className="flex shrink-0 items-center gap-2">
                  {it.status === "processing" ? <span className="flex items-center gap-1 text-indigo-700"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {it.progress ?? "üretiliyor"}</span>
                    : it.status === "error" ? <>
                        <span className="flex items-center gap-1 text-red-600"><AlertCircle className="h-3.5 w-3.5" /> hata</span>
                        <button onClick={() => void act(it, "/retry")} title="Tekrar dene" className="rounded border p-1 hover:bg-gray-50"><RotateCcw className="h-3 w-3" /></button>
                      </>
                    : <span className="text-gray-400">{it.progress ?? "bekliyor"}</span>}
                  {it.status !== "processing" && (
                    <button onClick={() => void act(it, "", "DELETE")} title="Kuyruktan çıkar" className="rounded border p-1 text-gray-400 hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {drafts !== null && missing.length === 0 && items.length === 0 && !error && (
        <p className="flex items-center gap-1 border-t px-4 py-3 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Tüm listelerin videosu var.</p>
      )}
    </div>
  );
}
